/**
 * MatchCard - Card displaying match information
 *
 * Styled like Overview's PhotoCard with status indicators,
 * team info, and assignment status.
 */

import clsx from 'clsx';
import { formatDateTime, getMatchStatus, getRelativeTime } from '../../utils/dateUtils';
import {
  getEventColor,
  getCompetitionLabel,
  MATCH_STATUS_COLORS,
  ASSIGNMENT_STATUS,
} from '../../utils/constants';
import styles from './MatchCard.module.css';

/**
 * Status pill component
 */
function StatusPill({ status }) {
  const color = MATCH_STATUS_COLORS[status] || '#888';
  const labels = {
    upcoming: 'Upcoming',
    live: 'LIVE',
    past: 'Finished',
    cancelled: 'Cancelled',
  };

  return (
    <span className={styles.statusPill} style={{ borderColor: color, color }}>
      <span className={styles.statusDot} style={{ background: color }} />
      {labels[status] || status}
    </span>
  );
}

/**
 * Assignment indicator
 */
function AssignmentBadge({ photographerCount, editorCount }) {
  const hasPhotographer = photographerCount > 0;
  const hasEditor = editorCount > 0;

  const status = (() => {
    if (hasPhotographer && hasEditor) return 'fully_assigned';
    if (hasPhotographer || hasEditor) return 'partially_assigned';
    return 'unassigned';
  })();

  const config = ASSIGNMENT_STATUS[status];

  return (
    <span className={styles.assignmentBadge} style={{ color: config.color }}>
      <span className={styles.assignIcon}>
        <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
          <path d="M20 21v-2a4 4 0 0 0-4-4H8a4 4 0 0 0-4 4v2" />
          <circle cx="12" cy="7" r="4" />
        </svg>
      </span>
      {photographerCount}
      {editorCount > 0 && (
        <>
          <span className={styles.assignDivider}>/</span>
          <span className={styles.assignIcon}>
            <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
              <path d="M11 4H4a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2v-7" />
              <path d="M18.5 2.5a2.121 2.121 0 0 1 3 3L12 15l-4 1 1-4 9.5-9.5z" />
            </svg>
          </span>
          {editorCount}
        </>
      )}
    </span>
  );
}

/**
 * Main MatchCard component
 */
export default function MatchCard({
  match,
  isSelected = false,
  onClick,
  compact = false,
}) {
  const status = getMatchStatus(match);
  const eventColor = getEventColor(match);
  const photographerCount = (match.photographer_assignments || []).length;
  const editorCount = (match.editor_assignments || []).length;
  const photoCount = match.photo_count || 0;

  const handleClick = () => {
    onClick?.(match);
  };

  const handleKeyDown = (e) => {
    if (e.key === 'Enter' || e.key === ' ') {
      e.preventDefault();
      handleClick();
    }
  };

  return (
    <article
      className={clsx(
        styles.card,
        isSelected && styles.selected,
        compact && styles.compact,
        status === 'live' && styles.live
      )}
      role="button"
      tabIndex={0}
      onClick={handleClick}
      onKeyDown={handleKeyDown}
    >
      {/* Color indicator bar */}
      <div className={styles.colorBar} style={{ background: eventColor }} />

      <div className={styles.content}>
        {/* Teams / Event name */}
        <div className={styles.teams}>
          {match.home_team_name && match.away_team_name ? (
            <>
              <span className={styles.teamHome}>{match.home_team_name}</span>
              <span className={styles.vs}>vs</span>
              <span className={styles.teamAway}>{match.away_team_name}</span>
            </>
          ) : (
            <span className={styles.eventName}>{match.name || 'Untitled Event'}</span>
          )}
        </div>

        {/* Match name if teams are shown */}
        {match.name && match.home_team_name && (
          <div className={styles.matchName}>{match.name}</div>
        )}

        {/* Time and venue */}
        <div className={styles.timeRow}>
          <span className={styles.time}>
            {formatDateTime(match.start_time, { year: undefined })}
          </span>
          {status === 'upcoming' && (
            <span className={styles.relative}>{getRelativeTime(match.start_time)}</span>
          )}
        </div>

        {match.venue && <div className={styles.venue}>{match.venue}</div>}

        {/* Competition badge */}
        {match.competition && (
          <div className={styles.competition}>
            {getCompetitionLabel(match.competition)}
          </div>
        )}

        {/* Footer with status and stats */}
        <div className={styles.footer}>
          <StatusPill status={status} />
          <div className={styles.stats}>
            <AssignmentBadge
              photographerCount={photographerCount}
              editorCount={editorCount}
            />
            {photoCount > 0 && (
              <span className={styles.photoCount}>
                <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                  <rect x="3" y="3" width="18" height="18" rx="2" ry="2" />
                  <circle cx="8.5" cy="8.5" r="1.5" />
                  <polyline points="21 15 16 10 5 21" />
                </svg>
                {photoCount}
              </span>
            )}
          </div>
        </div>
      </div>
    </article>
  );
}
